const MINIMUM_VISIBLE_MS = 180;
const loaderBootTime = typeof performance !== 'undefined' ? performance.now() : 0;
const PROGRESS_DURATION_MS = 1200;

export function hasModifiedClick(event) {
  if (!event) return false;

  return Boolean(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0);
}

export function shouldHandleNavigation({ anchor, currentUrl = window.location, event } = {}) {
  if (!anchor?.href || hasModifiedClick(event)) return false;
  if (anchor.target && anchor.target !== '_self') return false;
  if (anchor.hasAttribute?.('download')) return false;

  const nextUrl = new URL(anchor.href, currentUrl.href);
  const hrefAttribute = anchor.getAttribute?.('href') || '';

  if (!/^https?:$/i.test(nextUrl.protocol)) return false;
  if (nextUrl.origin !== currentUrl.origin) return false;

  const isSameDocument =
    nextUrl.pathname === currentUrl.pathname && nextUrl.search === currentUrl.search;

  if (isSameDocument && hrefAttribute.startsWith('#')) return false;
  if (isSameDocument && nextUrl.hash) return false;

  return true;
}

function setLoadingState(isLoading) {
  document.documentElement.classList.toggle('is-page-loading', isLoading);
  document.documentElement.classList.toggle('is-page-loaded', !isLoading);
}

function revealLoader() {
  setLoadingState(true);
}

export function getHideDelay(now = loaderBootTime) {
  return Math.max(0, MINIMUM_VISIBLE_MS - now);
}

export function splitLoaderLabel(label = '') {
  return Array.from(label);
}

export function getProgressValue(elapsed, duration = PROGRESS_DURATION_MS) {
  const safeElapsed = Math.max(0, elapsed);
  const ratio = Math.min(1, safeElapsed / duration);

  return Math.min(0.92, 0.12 + ratio * 0.8);
}

function setProgress(progressBar, value) {
  if (!(progressBar instanceof HTMLElement)) return;

  progressBar.style.transform = `scaleX(${value})`;
}

function buildAnimatedWordmark(container) {
  if (!(container instanceof HTMLElement)) return;

  const label = container.dataset.loaderLabel || '';
  const glyphs = splitLoaderLabel(label);

  container.replaceChildren();

  glyphs.forEach((glyph, index) => {
    const span = document.createElement('span');
    span.className = glyph === ' ' ? 'page-loader__space' : 'page-loader__glyph';
    span.textContent = glyph;
    span.style.setProperty('--glyph-index', `${index}`);
    container.append(span);
  });
}

function createProgressController(progressBar) {
  let frameId = 0;
  const startTime = typeof performance !== 'undefined' ? performance.now() : 0;

  const tick = () => {
    setProgress(progressBar, getProgressValue((typeof performance !== 'undefined' ? performance.now() : startTime) - startTime));
    frameId = window.requestAnimationFrame(tick);
  };

  tick();

  return {
    complete() {
      window.cancelAnimationFrame(frameId);
      setProgress(progressBar, 1);
    },
  };
}

function hideLoader(progressController) {
  const delay = getHideDelay(typeof performance !== 'undefined' ? performance.now() : MINIMUM_VISIBLE_MS);

  window.setTimeout(() => {
    progressController?.complete();
    window.setTimeout(() => {
      setLoadingState(false);
    }, 120);
  }, delay);
}

function handleDocumentClick(event) {
  const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (!anchor) return;

  if (!shouldHandleNavigation({ anchor, event })) return;
  revealLoader();
}

export function initPageLoader() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const loader = document.querySelector('[data-page-loader]');
  if (!(loader instanceof HTMLElement)) return;
  const wordmark = loader.querySelector('.page-loader__wordmark');
  const progressBar = loader.querySelector('[data-page-loader-progress]');

  document.documentElement.classList.add('has-page-loader');
  buildAnimatedWordmark(wordmark);
  const progressController = createProgressController(progressBar);

  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    hideLoader(progressController);
  } else {
    document.addEventListener('DOMContentLoaded', () => hideLoader(progressController), { once: true });
    window.addEventListener('load', () => hideLoader(progressController), { once: true });
  }

  document.addEventListener('click', handleDocumentClick);

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      progressController?.complete();
      setLoadingState(false);
      return;
    }

    hideLoader(progressController);
  });
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  initPageLoader();
}
